import axios from 'axios';
import { AxiosError } from 'axios';

// Flag za praćenje da li je redirekcija već u toku
let isRedirecting = false;

export const httpApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://reporting-api.diplomacy.edu',
});

// Request interceptor - dodaje token u header
httpApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('diploAccessKey');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper funkcija za logout i redirekciju
const handleLogout = (reason = 'Session expired', message = '') => {
  if (isRedirecting || window.location.pathname === "/login") {
    return;
  }
  
  isRedirecting = true;
  
  // Čišćenje podataka o sesiji
  localStorage.removeItem('diploAccessKey');
  localStorage.removeItem('diploAccessUser');
  
  // Čišćenje cookies ako postoje
  document.cookie.split(";").forEach((c) => {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
  
  console.log(`${reason}. Redirecting to login page...`);
  
  // Pokušaj da prikažeš notifikaciju korisniku (ako postoji sweetalert)
  if (window.swal) {
    window.swal({
      title: reason,
      text: message || 'You will be redirected to the login page.',
      icon: 'warning',
      timer: 2000,
      buttons: false
    });
  }
  
  // Koristite setTimeout za odlaganje redirekcije
  setTimeout(() => {
    window.location.href = "/login";
    isRedirecting = false; // Reset flag after redirect
  }, 2000); // 2 seconds delay to show notification
};

// Response interceptor - handle-uje greške i istekle sesije
httpApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorData = error.response?.data;
    const statusCode = error.response?.status;
    
    // Obrada 401 Unauthorized - istekla sesija ili nevažeći token
    if (statusCode === 401) {
      // Proverite da li je greška došla od login zahteva
      const isLoginRequest = error.config?.url?.includes('/api/auth/login');
      
      if (isLoginRequest) {
        // Za login zahteve, vratite detaljniju poruku greške
        let loginErrorMessage = 'Invalid login credentials';
        
        console.log('Login error response data:', errorData);
        
        if (errorData?.detail) {
          // FastAPI obično koristi 'detail' za error poruke
          if (typeof errorData.detail === 'string') {
            loginErrorMessage = errorData.detail;
          }
        } else if (errorData?.message) {
          loginErrorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          loginErrorMessage = errorData;
        }
        
        // Prevedemo česte poruke na engleski ako su na drugom jeziku
        if (loginErrorMessage.includes('korisničko ime') || loginErrorMessage.includes('username')) {
          loginErrorMessage = 'Incorrect username or password';
        }
        
        console.log('Final login error message:', loginErrorMessage);
        return Promise.reject(new Error(loginErrorMessage));
      }
      
      // Za ostale 401 greške (istekla sesija), preusmerite na login
      handleLogout('Session expired');
      return Promise.reject(new Error('Session expired. Redirecting to login...'));
    }
    
    // Obrada 403 Forbidden - korisnik nema pristup ili je sesija nevažeća
    if (statusCode === 403) {
      console.error('403 Forbidden: Access denied. Logging out user.');
      handleLogout(
        'Access Denied', 
        'You do not have permission to access this resource. Please login again.'
      );
      return Promise.reject(new Error('Access denied. You do not have permission to access this resource.'));
    }
    
    // Za ostale greške, propagirajte standardnu Error
    return Promise.reject(new Error(errorData?.detail || errorData?.message || error.message));
  }
);

// Helper funkcije za česte HTTP metode
export const apiClient = {
  get: (url, config) => httpApi.get(url, config),
  post: (url, data, config) => httpApi.post(url, data, config),
  put: (url, data, config) => httpApi.put(url, data, config),
  delete: (url, config) => httpApi.delete(url, config),
  patch: (url, data, config) => httpApi.patch(url, data, config),
};
